/* eslint-disable max-lines-per-function, max-statements */
import { AddNewsContext } from "contexts/MightyTool";
import { DEFAULT_NEWS } from "contexts/NewsCritics";
import useAddEditNews from "hooks/useAddEditNews";
import useCreateNewsCritics from "hooks/useCreateNewsCritics";
import useNewsCritics from "hooks/useNewsCritics";
import usePreviewsNewsCritics from "hooks/usePreviewsNewsCritics";
import equals from "ramda/src/equals";
import { useCallback, useContext, useEffect, useMemo } from "react";
import useIsImdbIdValid from "../useIsImdbIdValid";
import { getIsNewsReadyToBeCreated, useIsAddNewsEnabled } from "./helpers";
/**
 * useAddNews
 *
 * @description Hook to add a News
 * @param {UseAddNewsProps} props - Props
 * @param {UseAddNewsProps} props.IMDbId - IMDbId
 * @param {UseAddNewsProps} props.isAddNewsEnabled - Is Add News enabled
 * @param {UseAddNewsProps} props.isImdbIdValid - Is IMDbId valid
 * @param {UseAddNewsProps} props.loading - Is loading
 * @param {UseAddNewsProps} props.name - Name
 * @param {UseAddNewsProps} props.onAddHomeVideoReview - On Add News
 * @param {UseAddNewsProps} props.onClearAll - On Clear All
 * @returns {UseAddNewsReturn}
 *
 */
const useAddNews = (props) => {
    const { resetImages } = usePreviewsNewsCritics();
    const { addNews, called: addNewsCalled, data, loading: addNewsLoading, reset } = useContext(AddNewsContext);
    // NewsCritics State
    const { clearAll, resetSelectedNewsCritics, setAdditionalInstructions, state: { avNirvana, bluRay, hidefDigest, news, newsSourcesIdsServiceMap, singleSource, tmdbSelectedImdbId, whySoBlu } } = useNewsCritics();
    // Create NewsCritics State
    const { areNewsSourcesCalled, areNewsSourcesLoading, onCreateNewsSource, resetNewsSources } = useCreateNewsCritics();
    // Clear All
    const { onClearAll: onClearAllAddEditNews } = useAddEditNews();
    /**
     * Are all News properties ready?
     */
    const isNewsReadyToBeCreated = useMemo(() => getIsNewsReadyToBeCreated({
        avNirvana: avNirvana.selected,
        bluRay: bluRay.selected,
        hidefDigest: hidefDigest.selected,
        newsSourcesIdsServiceMap,
        singleSource,
        whySoBlu: whySoBlu.selected
    }), [
        avNirvana.selected,
        bluRay.selected,
        hidefDigest.selected,
        newsSourcesIdsServiceMap,
        singleSource,
        whySoBlu.selected
    ]);
    /**
     * Effective IMDb ID: TMDB selection supersedes critic review selection
     */
    const effectiveImdbId = tmdbSelectedImdbId ?? news.IMDbId;
    /**
     * If reviews are called and News is ready to be created and
     * create hasn't been called then create a News
     */
    useEffect(() => {
        if (props?.isReadOnly) {
            return;
        }
        if (areNewsSourcesCalled) {
            if (isNewsReadyToBeCreated && !addNewsCalled) {
                addNews({
                    variables: {
                        news: {
                            ...news,
                            IMDbId: news.isAddHomeVideo ? effectiveImdbId : undefined,
                            isPublished: false
                        }
                    }
                });
            }
        }
    }, [
        addNews,
        addNewsCalled,
        areNewsSourcesCalled,
        effectiveImdbId,
        isNewsReadyToBeCreated,
        news,
        props?.isReadOnly
    ]);
    // If News has been created then reset reviews
    useEffect(() => {
        if (props?.isReadOnly) {
            return;
        }
        if (data) {
            if (!equals(news, DEFAULT_NEWS)) {
                resetSelectedNewsCritics();
                reset();
            }
            // Reset images
            resetImages();
            // Reset news critics
            resetNewsSources();
            // Clear all reviews
            clearAll();
        }
    }, [
        clearAll,
        data,
        news,
        props?.isReadOnly,
        reset,
        resetImages,
        resetNewsSources,
        resetSelectedNewsCritics
    ]);
    // Are we adding news or news sources?
    const loading = useMemo(() => addNewsLoading || areNewsSourcesLoading, [addNewsLoading, areNewsSourcesLoading]);
    /**
     * isImdbIdValid
     *
     * @description Check if IMDbId is valid
     */
    const isImdbIdValid = useIsImdbIdValid(effectiveImdbId);
    // Are all News properties ready for the create button to be enabled?
    const isAddNewsEnabled = useIsAddNewsEnabled({
        isImdbIdValid,
        loading,
        news,
        services: {
            avNirvana: avNirvana.selected,
            bluRay: bluRay.selected,
            hidefDigest: hidefDigest.selected,
            whySoBlu: whySoBlu.selected
        }
    });
    /**
     * onAddNews
     *
     * @description
     * Handles the addition of a news item.
     * @param {object} [options] - Optional parameters.
     * @param {string} [options.additionalInstructions] - Additional instructions for creating the news item.
     * @returns {void}
     *
     */
    const onAddNews = useCallback((options) => {
        if (options?.additionalInstructions) {
            setAdditionalInstructions(options.additionalInstructions);
        }
        onCreateNewsSource();
    }, [onCreateNewsSource, setAdditionalInstructions]);
    /**
     * onClearAll
     *
     * @description
     * Clears all fields and resets the state related to adding a news item.
     * @returns {void}
     *
     */
    const onClearAll = useCallback(() => {
        onClearAllAddEditNews();
    }, [onClearAllAddEditNews]);
    return {
        IMDbId: effectiveImdbId,
        isAddNewsEnabled,
        isImdbIdValid,
        loading,
        name: news.name,
        onAddNews,
        onClearAll
    };
};
export default useAddNews;
